import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Network,
  ArrowRight,
  Leaf,
  Brain,
  Users,
  Coffee,
  Recycle,
  TrendingUp,
  Play,
  Database,
  HelpCircle,
  CheckCircle,
  Shield,
  Globe,
  Zap,
  Smartphone,
  Scan,
  LineChart,
  ChevronRight,
  BarChart3,
  Sprout,
  Bug
} from 'lucide-react';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import SectionHeader from '../components/shared/SectionHeader';
import FeatureCard from '../components/shared/FeatureCard';
import CtaSection from '../components/shared/CtaSection';
import MarketPreview from '../components/MarketPreview';
import UserDistributionMap from '../components/UserDistributionMap';
import TechSection from '../components/shared/TechSection';
import FarmerImpactSection from '../components/shared/FarmerImpactSection';
import { Card, Button, Badge } from '../components/ui';

const HomePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('hybrid');
  const [activeFeature, setActiveFeature] = useState(0);
  
  const features = [
    {
      title: "AI-Powered Traceability",
      description: "Track every step from seed to shelf with our advanced AI system that creates an immutable record of ethical practices and fair compensation.",
      icon: <Scan className="h-8 w-8 text-avn-purple-600" />
    },
    {
      title: "Regenerative Farming",
      description: "Combine agroforestry, controlled environment agriculture, and circular inputs to maximize yields while restoring soil health.",
      icon: <Leaf className="h-8 w-8 text-green-600" />
    },
    {
      title: "Farmer Data Ownership",
      description: "Farmers own their production, labor, and impact data — and can monetize it on the AVN network.",
      icon: <Shield className="h-8 w-8 text-blue-600" />
    }
  ];
  
  const stats = [
    { value: "45%", label: "Yield Increase" },
    { value: "30%", label: "Water Savings" },
    { value: "25-50%", label: "Value Returned to Farmers" },
    { value: "100%", label: "Supply Chain Visibility" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative min-h-[90vh]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1605000797499-95a51c5269ae"
            alt="Agricultural landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/80" />
        </div>
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <AnimatedSection>
            <div className="max-w-3xl pt-32">
              <Badge icon={<Network className="h-4 w-4" />} variant="purple" className="px-4 py-2 text-sm mb-8 bg-avn-purple-600 text-white">
                Agricultural Value-chain Network
              </Badge>
              <h1 className="text-6xl font-bold text-white mb-8 leading-tight">
                Transforming Agriculture Through Ethical Innovation
              </h1>
              <p className="text-xl text-white/90 mb-12 leading-relaxed">
                AVN is dedicated to ending exploitation in global food production through our groundbreaking technology, while promoting sustainable and ethical agricultural practices worldwide.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  variant="primary" 
                  size="lg" 
                  icon={<Network className="h-6 w-6" />} 
                  className="bg-[#4B0082] hover:bg-[#3b0065]"
                >
                  <Link to="/signup" className="text-white">Join AVN Network</Link>
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  icon={<ArrowRight className="h-6 w-6" />} 
                  className="bg-white text-[#4B0082] border-white hover:bg-white/90"
                >
                  <Link to="/avn" className="text-[#4B0082]">How AVN Works</Link>
                </Button>
              </div>
            </div>
            
          </AnimatedSection>
        </div>
      </div>
      
      {/* Floating Stats Card */}
      <div className="relative z-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto -mt-24">
            <div className="bg-white rounded-xl shadow-xl p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-4">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-4xl font-bold text-avn-purple-600 mb-2">{stat.value}</div>
                    <div className="text-gray-700">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Technology Section with AVN.AI illustration */}
      <TechSection />

      {/* Farmer Impact Section with Farming Photos */}
      <FarmerImpactSection />

      {/* Core Features Section */}
      <div className="py-32 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Core Features"
            subtitle="Our comprehensive platform transforms every step of the agricultural value chain."
            icon={<Zap className="h-5 w-5" />}
            badgeText="What We Offer"
            badgeColor="purple"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto mt-16">
            {/* Feature Tabs */}
            <div className="space-y-6">
              {features.map((feature, index) => (
                <AnimatedSection key={index} delay={index * 0.1}>
                  <Card 
                    interactive={true} 
                    onClick={() => setActiveFeature(index)}
                    className={`transition-all ${
                      activeFeature === index 
                        ? 'shadow-xl border-l-4 border-l-avn-purple-600' 
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${
                        activeFeature === index ? 'bg-avn-purple-50' : 'bg-white'
                      }`}>
                        {feature.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                        <p className="text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
            
            {/* Feature Image */}
            <AnimatedSection delay={0.3}>
              <div className="bg-gray-100 rounded-xl overflow-hidden h-full flex items-center justify-center">
                {activeFeature === 0 ? (
                  <img 
                    src="/image copy.png"
                    alt="AI-Powered Traceability"
                    className="w-full h-full object-cover"
                  />
                ) : activeFeature === 1 ? (
                  <img 
                    src="https://images.unsplash.com/photo-1591857177580-dc82b9ac4e1e" 
                    alt="Regenerative Farming"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img 
                    src="/image copy copy.png" 
                    alt="Farmer Data Ownership"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* What Makes AVN Different */}
      <div className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="What Makes AVN Different"
            subtitle="We don't extract value. We return it — ethically, transparently, and regeneratively."
            icon={<Brain className="h-5 w-5" />}
            badgeText="Our Approach"
            badgeColor="purple"
          />

          <div className="max-w-5xl mx-auto mt-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <AnimatedSection delay={0.1}>
                <Card
                  icon={<ArrowRight className="h-6 w-6 text-red-600" />}
                  title="From extractive to equitable"
                  className="h-full"
                >
                  <p className="text-gray-600">
                    AVN rewires the entire supply chain to ensure farmers receive 25-50% of the final value, compared to just 6% in traditional models.
                  </p>
                </Card>
              </AnimatedSection>
              
              <AnimatedSection delay={0.2}>
                <Card
                  icon={<Scan className="h-6 w-6 text-blue-600" />}
                  title="From invisible to traceable"
                  className="h-full"
                >
                  <p className="text-gray-600">
                    Our AI-powered traceability system creates complete transparency across the entire agricultural supply chain.
                  </p>
                </Card>
              </AnimatedSection>
              
              <AnimatedSection delay={0.3}>
                <Card
                  icon={<Sprout className="h-6 w-6 text-green-600" />}
                  title="From monoculture to regenerative"
                  className="h-full"
                >
                  <p className="text-gray-600">
                    Our hybrid farming approach combines traditional wisdom with cutting-edge technology to restore soil health and biodiversity.
                  </p>
                </Card>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </div>

      {/* Unity Bean Case Study */}
      <div className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Unity Bean: Our Flagship Implementation"
            subtitle="See AVN in action through our premium coffee grown in Uganda."
            icon={<Coffee className="h-5 w-5" />}
            badgeText="Case Study"
            badgeColor="amber"
          />
          
          <div className="max-w-5xl mx-auto mt-16">
            <Card className="p-0 overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="p-8">
                  <Badge icon={<Coffee className="h-4 w-4" />} variant="amber" className="mb-6">
                    From Farm to Cup
                  </Badge>
                  
                  <h3 className="text-2xl font-bold mb-6">The Unity Bean Journey</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-amber-100 rounded-full mt-1">
                        <Users className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <span className="font-medium">273 Smallholder Farmers</span>
                        <p className="text-gray-600 text-sm">Growing coffee using regenerative practices in Uganda.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-amber-100 rounded-full mt-1">
                        <Scan className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <span className="font-medium">100% Traceable</span>
                        <p className="text-gray-600 text-sm">Every bean verified through AVN-Earth AI from soil to shelf.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-amber-100 rounded-full mt-1">
                        <Recycle className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <span className="font-medium">Value-Added Processing</span>
                        <p className="text-gray-600 text-sm">Coffee cherry syrup and other byproducts create additional revenue.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-amber-100 rounded-full mt-1">
                        <TrendingUp className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <span className="font-medium">25-50% Farmer Revenue</span>
                        <p className="text-gray-600 text-sm">Compared to just 6% in traditional coffee supply chains.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8">
                    <Link
                      to="/unity-bean"
                      className="inline-flex items-center gap-2 text-amber-600 font-medium hover:text-amber-700 transition-colors"
                    >
                      Learn more about Unity Bean
                      <ChevronRight className="h-5 w-5" />
                    </Link>
                  </div>
                </div>
                
                <div className="h-full">
                  <img 
                    src="/image.png" 
                    alt="Unity Bean Coffee Shop"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Global Map Section */}
      <div className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="AVN Global Network"
            subtitle="Join our growing community of farmers, buyers, and partners across the world."
            icon={<Globe className="h-5 w-5" />}
            badgeText="Live Data"
            badgeColor="blue"
          />

          <div className="max-w-6xl mx-auto mt-16">
            <UserDistributionMap />
          </div>
        </div>
      </div>

      {/* Market Intelligence Preview */}
      <div className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeader
            title="Real-Time Market Intelligence"
            subtitle="Stay ahead with AI-powered insights and analytics for agricultural markets."
            icon={<LineChart className="h-5 w-5" />}
            badgeText="Live Data"
            badgeColor="green"
          />

          <div className="max-w-6xl mx-auto mt-16">
            <MarketPreview />
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <CtaSection
        title="Ready to Transform Your Agricultural Future?"
        subtitle="Join the movement that puts transparency, equity, and resilience at the heart of agriculture."
        primaryButtonText="Join the Movement"
        primaryButtonIcon={<Network className="h-6 w-6" />}
        primaryButtonLink="/signup"
        secondaryButtonText="Watch Demo"
        secondaryButtonIcon={<Play className="h-6 w-6" />}
        secondaryButtonLink="/demo"
      />
    </div>
  );
};

export default HomePage;